import { useMemo, useCallback } from 'react';

/**
 * Unified utilities for officer display, selection, and calculations.
 * Replaces 20+ scattered implementations of officer-related logic.
 * 
 * Usage:
 * const {
 *   getOfficer,
 *   getOfficerDisplay,
 *   getOfficerLevel,
 *   getOfficerOptions,
 *   filterHumanOfficers,
 *   filterBotOfficers,
 *   isCommander,
 *   isSeniorOfficer
 * } = useOfficerUtils({ officers, currentUserEmail });
 */

// XP per level constant
export const XP_PER_LEVEL = 500;

// Commander roles
export const COMMANDER_ROLES = ['Commander in Chief', 'Commander'];
export const SENIOR_ROLES = ['Commander in Chief', 'Commander', 'Senior Officer'];

/**
 * Calculate level from XP
 */
export function calculateLevel(xp) {
  return Math.floor((xp || 0) / XP_PER_LEVEL) + 1;
}

/**
 * Calculate XP progress within current level (0-100%)
 */
export function calculateLevelProgress(xp) {
  const currentXP = (xp || 0) % XP_PER_LEVEL;
  return (currentXP / XP_PER_LEVEL) * 100;
}

/**
 * Get XP needed for next level
 */
export function xpToNextLevel(xp) {
  return XP_PER_LEVEL - ((xp || 0) % XP_PER_LEVEL);
}

/**
 * Main officer utilities hook
 */
export default function useOfficerUtils({ 
  officers = [], 
  currentUserEmail = null,
  currentUserRole = null 
}) {
  
  // Get officer by email
  const getOfficer = useCallback((email) => {
    if (!email) return null;
    return officers.find(o => o.user_email === email);
  }, [officers]);

  // Get current user's officer profile
  const currentOfficer = useMemo(() => {
    return getOfficer(currentUserEmail);
  }, [currentUserEmail, getOfficer]);

  // Check if current user is commander
  const isCommander = useMemo(() => {
    return currentUserRole === 'admin' || 
           COMMANDER_ROLES.includes(currentOfficer?.custom_role);
  }, [currentUserRole, currentOfficer]);

  // Check if current user is senior officer (commander or senior)
  const isSeniorOfficer = useMemo(() => {
    return currentUserRole === 'admin' || 
           SENIOR_ROLES.includes(currentOfficer?.custom_role);
  }, [currentUserRole, currentOfficer]);

  // Get officer display name
  const getOfficerDisplay = useCallback((email, options = {}) => {
    const { 
      includeLevel = true, 
      includeRank = true,
      includeBotIcon = true,
      fallbackToEmail = true,
      format = 'full' // 'full', 'short', 'callsign'
    } = options;

    if (!email) return 'Unknown';
    
    const officer = getOfficer(email);
    if (!officer) {
      return fallbackToEmail ? email.split('@')[0] : 'Unknown';
    }

    const isBot = officer.is_bot || officer.custom_role === 'AI Officer';
    const botIcon = includeBotIcon && isBot ? '🤖 ' : '';
    const level = calculateLevel(officer.total_xp || 0);

    switch (format) {
      case 'callsign':
        return `${botIcon}${officer.callsign}`;
      
      case 'short':
        return `${botIcon}${officer.callsign}${includeLevel ? ` L${level}` : ''}`;
      
      case 'full':
      default:
        const rank = includeRank ? `${officer.rank_title} ` : '';
        const levelStr = includeLevel ? ` • Lvl ${level}` : '';
        return `${botIcon}${rank}${officer.callsign}${levelStr}`;
    }
  }, [getOfficer]);

  // Get officer level
  const getOfficerLevel = useCallback((email) => {
    const officer = getOfficer(email);
    return calculateLevel(officer?.total_xp || 0);
  }, [getOfficer]);

  // Check if officer is a bot
  const isBot = useCallback((emailOrOfficer) => {
    const officer = typeof emailOrOfficer === 'string' 
      ? getOfficer(emailOrOfficer) 
      : emailOrOfficer;
    return officer?.is_bot || officer?.custom_role === 'AI Officer';
  }, [getOfficer]);

  // Check if current user owns a bot
  const isBotOwner = useCallback((botEmail) => {
    const bot = getOfficer(botEmail);
    return bot?.bot_owner_email === currentUserEmail;
  }, [getOfficer, currentUserEmail]);

  // Filter human officers only
  const humanOfficers = useMemo(() => {
    return officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer');
  }, [officers]);

  // Filter bot officers only
  const botOfficers = useMemo(() => {
    return officers.filter(o => o.is_bot || o.custom_role === 'AI Officer');
  }, [officers]);

  // Get officers the current user can assign to (includes their bots)
  const assignableOfficers = useMemo(() => {
    return officers.filter(o => {
      // All human officers
      if (!o.is_bot && o.custom_role !== 'AI Officer') return true;
      // Bots owned by current user
      if (o.bot_owner_email === currentUserEmail) return true;
      return false;
    });
  }, [officers, currentUserEmail]);

  // Get options for officer selector
  const getOfficerOptions = useCallback((options = {}) => {
    const {
      excludeEmails = [],
      includeBots = true,
      onlyBots = false,
      onlyHumans = false,
      onlyOwnedBots = false,
      includeLevel = true
    } = options;

    let filtered = officers.filter(o => {
      if (!o.user_email) return false;
      if (excludeEmails.includes(o.user_email)) return false;
      
      const isBotOfficer = o.is_bot || o.custom_role === 'AI Officer';
      
      if (onlyBots && !isBotOfficer) return false;
      if (onlyHumans && isBotOfficer) return false;
      if (!includeBots && isBotOfficer) return false;
      if (onlyOwnedBots && (!isBotOfficer || o.bot_owner_email !== currentUserEmail)) return false;
      
      return true;
    });

    return filtered.map(o => {
      const isBotOfficer = o.is_bot || o.custom_role === 'AI Officer';
      const level = calculateLevel(o.total_xp || 0);
      
      let label = isBotOfficer 
        ? `🤖 ${o.callsign} (AI Officer)`
        : `${o.callsign}, ${o.rank_title}`;
      
      if (includeLevel) {
        label += `, L${level}`;
      }
      
      return {
        value: o.user_email,
        label,
        officer: o
      };
    });
  }, [officers, currentUserEmail]);

  // Check role permissions
  const hasRole = useCallback((requiredRoles) => {
    if (!currentOfficer) return false;
    if (currentUserRole === 'admin') return true;
    return requiredRoles.includes(currentOfficer.custom_role);
  }, [currentOfficer, currentUserRole]);

  // Get officers by role
  const getOfficersByRole = useCallback((roles) => {
    return officers.filter(o => roles.includes(o.custom_role));
  }, [officers]);

  // Get commanders
  const commanders = useMemo(() => {
    return getOfficersByRole(COMMANDER_ROLES);
  }, [getOfficersByRole]);

  return {
    // Lookups
    getOfficer,
    getOfficerDisplay,
    getOfficerLevel,
    isBot,
    isBotOwner,
    
    // Current user
    currentOfficer,
    isCommander,
    isSeniorOfficer,
    hasRole,
    
    // Filtered lists
    humanOfficers,
    botOfficers,
    assignableOfficers,
    commanders,
    
    // Selection helpers
    getOfficerOptions,
    getOfficersByRole,
    
    // Calculations
    calculateLevel,
    calculateLevelProgress,
    xpToNextLevel,
    
    // Constants
    XP_PER_LEVEL,
    COMMANDER_ROLES,
    SENIOR_ROLES
  };
}

/**
 * Standalone function for contexts where hook isn't appropriate
 */
export function formatOfficerDisplay(officer, options = {}) {
  if (!officer) return 'Unknown';
  
  const { 
    includeLevel = true, 
    includeRank = true,
    includeBotIcon = true 
  } = options;

  const isBot = officer.is_bot || officer.custom_role === 'AI Officer';
  const botIcon = includeBotIcon && isBot ? '🤖 ' : '';
  const level = calculateLevel(officer.total_xp || 0);
  const rank = includeRank ? `${officer.rank_title} ` : '';
  const levelStr = includeLevel ? ` • Lvl ${level}` : '';

  return `${botIcon}${rank}${officer.callsign}${levelStr}`;
}